/**
 * soundEffects.ts — public facade for all game audio.
 *
 * Sounds are synthesised procedurally in lib/audio/synth.ts, baked to WAV data
 * URIs and played through Howler in lib/audio/player.ts (no shipped audio
 * files). This file exposes the stable `sfx*` functions used across the app so
 * callers don't need to know how sounds are produced.
 *
 * (The generated `.m4a`/`.mp3` clips in public/audio/sfx and their
 * lib/audio/soundManifest.ts map are retained but currently UNUSED — kept in
 * case we return to file-based playback.)
 */

import {
  playSound,
  startBattleMusic,
  stopBattleMusic,
  startTheme,
  stopTheme,
  isAudioMuted,
  setAudioMuted,
  subscribeAudioMuted,
} from './audio/player';

// ─── Battle — result jingles ──────────────────────────────────────────────────

/** Victory: ascending C major arpeggio. */
export const sfxWin = () => playSound('win');
/** Defeat: descending minor phrase. */
export const sfxLose = () => playSound('lose');

// ─── Battle — in-fight sounds ─────────────────────────────────────────────────

/** Countdown tick for 3, 2, 1. */
export const sfxCountdownTick = () => playSound('countdownTick');
/** Countdown hits 0. */
export const sfxCountdownGo = () => playSound('countdownGo');
/** Player's attack lands on opponent. */
export const sfxAttackHit = () => playSound('attackHit');
/** Opponent's attack lands on player. */
export const sfxPlayerHit = () => playSound('playerHit');
/** A stack lands as a combo, timed to its beat: ×3 three blows, ×4 the charge, three blows and the finisher. */
export const sfxComboStrike = (hits: number) => playSound(hits >= 4 ? 'comboStrike' : 'comboStrike3');
/** HP is restored. */
export const sfxHealEffect = () => playSound('heal');

// ─── Care — pet action sounds ─────────────────────────────────────────────────

/** Meat feed. */
export const sfxFeedMeat = () => playSound('feedMeat');
/** Snack feed. */
export const sfxFeedSnack = () => playSound('feedSnack');
/** Full bath. */
export const sfxClean = () => playSound('clean');
/** Single poop removed. */
export const sfxPartialClean = () => playSound('partialClean');
/** Medicine. */
export const sfxMedicine = () => playSound('medicine');
/** Starting to rest. */
export const sfxRest = () => playSound('rest');
/** Waking up. */
export const sfxWakeUp = () => playSound('wakeUp');
/** Energy refill. */
export const sfxRefill = () => playSound('refill');

// ─── Care — training wheel sounds ─────────────────────────────────────────────

/**
 * Roulette ratchet that slows to a stop. The clip is baked to match the wheel
 * animation length, so the `durationMs` argument is accepted for call-site
 * compatibility but no longer used.
 */
export const sfxWheelSpin = (_durationMs?: number) => playSound('wheelSpin');
/** XP bonus chime. */
export const sfxWheelResultXP = () => playSound('wheelResultXP');
/** Prize (XP + Stats) fanfare. */
export const sfxWheelResultPrize = () => playSound('wheelResultPrize');
/** Sick result. */
export const sfxWheelResultSick = () => playSound('wheelResultSick');
/** Suika reward. */
export const sfxWheelResultSuika = () => playSound('wheelResultSuika');
/** No bonus. */
export const sfxWheelResultNothing = () => playSound('wheelResultNothing');

// ─── Care minigames (Fruit Catch / Rhythm Training / Bubble Scrub) ────────────

/** Minigame begins — "ready… go!". */
export const sfxMinigameStart = () => playSound('minigameStart');
/** Successful hit (fruit caught / beat on time). */
export const sfxMinigameHit = () => playSound('minigameHit');
/** Bubble popped (Bubble Scrub). */
export const sfxMinigamePop = () => playSound('minigamePop');
/** Miss / off-beat. */
export const sfxMinigameMiss = () => playSound('minigameMiss');
/** Tier fanfare, escalating with the result (none plays no sound). */
export const sfxMinigameTier = (tier: 'none' | 'bronze' | 'silver' | 'gold' | 'perfect') => {
  if (tier === 'bronze') playSound('minigameTierBronze');
  else if (tier === 'silver') playSound('minigameTierSilver');
  else if (tier === 'gold') playSound('minigameTierGold');
  else if (tier === 'perfect') playSound('minigameTierPerfect');
};

// ─── Milestones — evolution, level up, adoption, eggs ─────────────────────────

/** Pet evolves or hatches (reveal moment). */
export const sfxEvolve = () => playSound('evolve');
/** Pet levels up. */
export const sfxLevelUp = () => playSound('levelUp');
/** Adoption/mint completes successfully. */
export const sfxAdoptSuccess = () => playSound('adoptSuccess');
/** Egg is tapped to hatch. */
export const sfxEggHatch = () => playSound('eggHatch');
/** New egg is created via breeding. */
export const sfxEggCreated = () => playSound('eggCreated');

// ─── Notifications — generic toast + PVP matchmaking ──────────────────────────

/** Generic success toast. */
export const sfxToastSuccess = () => playSound('toastSuccess');
/** Generic error toast. */
export const sfxToastError = () => playSound('toastError');
/** PVP opponent found. */
export const sfxMatchFound = () => playSound('matchFound');
/**
 * Post-battle reward chest opened.
 *
 * Reuses the `wheelResultPrize` clip — the existing "cheerful reward jingle" (see
 * docs/SOUND_DESIGN.md) — because the chest is the same beat: a reward being revealed.
 * Named separately so a bespoke chest clip is a one-line swap here rather than a hunt
 * through the components.
 */
export const sfxRewardChestOpen = () => playSound('wheelResultPrize');

/**
 * One KNOCK on a still-closed reward chest — the taps before the last one.
 *
 * Reuses the minigame's short hit clip: it is a percussive tick with no resolution to it,
 * which is exactly what a knock needs to be. The chest's jingle
 * ({@link sfxRewardChestOpen}) is saved for the tap that actually opens it, so the beats
 * stay distinguishable by ear alone.
 */
export const sfxRewardChestKnock = () => playSound('minigameHit');

// ─── Battle background music ──────────────────────────────────────────────────

export { startBattleMusic, stopBattleMusic, startTheme, stopTheme };

// ─── Global mute preference (player choice, persisted) ────────────────────────

export { isAudioMuted, setAudioMuted, subscribeAudioMuted };
